import type { ConnectError } from '@connectrpc/connect'
import { createConnectQueryKey, useMutation } from '@connectrpc/connect-query'
import {
  getGenerationSalesPlanDetail,
  submitGenerationSalesPlan,
} from '@ebalance/proto/bffuser/v1/generation_sales_plan_service-GenerationSalesPlanService_connectquery'
import type { SubmitGenerationSalesPlanResponse } from '@ebalance/proto/bffuser/v1/generation_sales_plan_service_pb'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { useErrorToast } from '~/hooks/useErrorToast'
import { useSuccessToast } from '~/hooks/useSuccessToast'
import { isNetworkError } from '~/libs/isNetworkError'

/**
 * 発電販売計画 提出 mutation hook。
 *
 * `GenerationSalesPlanService.SubmitGenerationSalesPlan` を呼ぶ。
 * onSuccess で当該 plan の GetGenerationSalesPlanDetail を invalidate し、
 * 詳細画面のステータス・提出日時を即時反映する。成功 / 失敗いずれも toast で通知する
 * (= AC-035/AC-036: 成功時はステータス「提出済」、失敗時は「提出失敗」に遷移するため、
 * toast は結果の速報のみを担い、正の canonical な状態は再取得した detail が持つ)。
 */
export function useSubmitGenerationSalesPlan(planId: string): {
  submit: () => void
  isSubmitting: boolean
} {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const showSuccessToast = useSuccessToast()
  const showErrorToast = useErrorToast()

  const { mutate, isPending } = useMutation(submitGenerationSalesPlan, {
    onSuccess: async (
      res: SubmitGenerationSalesPlanResponse,
    ): Promise<void> => {
      await queryClient.invalidateQueries({
        queryKey: createConnectQueryKey({
          schema: getGenerationSalesPlanDetail,
          cardinality: 'finite',
        }),
      })
      if (res.errorReason !== undefined) {
        showErrorToast({
          title: t('page:generationSalesPlanDetail.submit.errorTitle'),
          description: res.errorReason,
        })
        return
      }
      showSuccessToast({
        title: t('page:generationSalesPlanDetail.submit.successTitle'),
      })
    },
    onError: (err: ConnectError): void => {
      showErrorToast({
        title: t('page:generationSalesPlanDetail.submit.errorTitle'),
        description: isNetworkError(err)
          ? t('page:generationSalesPlanDetail.submit.networkErrorDescription')
          : err.message,
      })
    },
  })

  const submit = (): void => {
    mutate({ id: planId })
  }

  return { submit, isSubmitting: isPending }
}
